import { useEffect, useState } from 'react';
import {
  collection,
  documentId,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import {
  DailyPostHogSchema,
  type DailyPostHog,
  type Period,
} from '@etiquetachef/types';
import { firestore } from '@/lib/firebase';
import { isReportExcludedTenant } from '@/features/tenants/testTenantFilter';
import { reportNonFatal, serializeError } from '@/lib/errorReport';

const TAG = 'admin/product/useProduct';
const D = 24 * 60 * 60 * 1000;

export type TtfBucket = 'LT_1H' | 'D_1H_24H' | 'D_1_7D' | 'D_8_30D' | 'GT_30D' | 'NEVER';

export type ProductState =
  | { kind: 'LOADING' }
  | { kind: 'ERROR'; error: string }
  | {
      kind: 'READY';
      posthog: DailyPostHog[];
      timeToFirstLabel: Record<TtfBucket, number>;
      stickinessLatestPct: number | null;
    };

function bucketTtf(diffMs: number | null): TtfBucket {
  if (diffMs === null) return 'NEVER';
  if (diffMs < 1 * 60 * 60 * 1000) return 'LT_1H';
  if (diffMs < 24 * 60 * 60 * 1000) return 'D_1H_24H';
  if (diffMs < 7 * D) return 'D_1_7D';
  if (diffMs < 30 * D) return 'D_8_30D';
  return 'GT_30D';
}

/**
 * Carrega:
 *  - DAU/WAU/MAU série temporal de /aggregations/posthog/days/
 *    no range do period.
 *  - histograma time-to-first-label: para cada tenant, calcula
 *    `firstLabelAt - createdAt` e classifica em buckets. Tenants
 *    sem `firstLabelAt` caem em NEVER.
 *  - stickiness do dia mais recente.
 *
 * Origem: sem issue — commit 894180bae, 2026-05-04 (rota /dashboard/product).
 *
 * Contrato: `period` (datas `YYYY-MM-DD`) → `ProductState`, leitura one-shot
 * por mudança de período. Doc PostHog fora do schema é pulado só com
 * `console.warn`; erro → `ERROR` só com `console.error` — sem report ao
 * PostHog (achado registrado na #926). O histograma ignora o período: varre
 * os tenants (inclusive demo e excluídos) a cada carga — MENOS os de teste
 * (#1008: cadastro `testTenants.ts`, por `isReportExcludedTenant` só com o id;
 * sandbox não cadastrado continua contando, aceito na #1008). DAU/WAU/MAU e
 * stickiness vêm do agregado `aggregatePostHog`, filtrado na Function (#1008
 * item 6), não aqui.
 *
 * Usado por: `ProductDashboardPage.tsx` (rota `/dashboard/product`; web
 * `apps/admin`, super-admin), que repassa a `DauWauMauChart`,
 * `StickinessCard` e `TimeToFirstLabelHistogram` (este importa `TtfBucket`).
 *
 * Arquitetura: só leitura de coleções diretas, sem collection group:
 * `aggregations/posthog/days` via `match /aggregations/{type}/{subcoll}/{id}`
 * e `tenants` pelo match de tenant (super-admin), ambos em
 * `firebase/firestore.rules`. Escritores: `aggregatePostHog` e
 * `firstLabelPrinted` (Functions).
 *
 * Dados (anonimizado):
 *   aggregations/posthog/days/{yyyymmdd} — `DailyPostHogSchema`
 *     (`packages/types/src/aggregations.ts`): `{ "yyyymmdd": "20261007",
 *     "schemaVersion": 1, "dau": 40, "wau": 90, "mau": 150,
 *     "stickinessPct": 0.27, "computedAt": 1759300000000,
 *     "computedBy": "aggregatePostHog" }` — `stickinessPct` em fração 0–1;
 *     `null` quando o PostHog não respondeu.
 *   tenants/{tenantId} → `createdAt` e `firstLabelAt`, ambos em ms.
 *
 * Dicas: o custo cresce com o número de tenants (lê a coleção inteira).
 *
 * Issues: #926 (achados), #1008 (tenant de teste fora).
 *
 * Histórico:
 *  - 2026-10-19 20:57 +0200 — (#1008) tenant de teste fora do histograma.
 */
export function useProduct(period: Period): ProductState {
  const [state, setState] = useState<ProductState>({ kind: 'LOADING' });

  const periodFrom = period.from;
  const periodTo = period.to;

  useEffect(() => {
    const guard = { cancelled: false };
    setState({ kind: 'LOADING' });

    async function load(): Promise<void> {
      try {
        const fromCompact = periodFrom.replace(/-/g, '');
        const toCompact = periodTo.replace(/-/g, '');

        // PostHog daily.
        const phRef = collection(firestore, 'aggregations', 'posthog', 'days');
        const phSnap = await getDocs(
          query(
            phRef,
            where(documentId(), '>=', fromCompact),
            where(documentId(), '<=', toCompact),
          ),
        );
        const posthog: DailyPostHog[] = [];
        for (const d of phSnap.docs) {
          const parsed = DailyPostHogSchema.safeParse(d.data());
          if (parsed.success) posthog.push(parsed.data);
          else console.warn(`${TAG}: ${d.id} ignorado`, parsed.error.issues);
        }
        posthog.sort((a, b) => a.yyyymmdd.localeCompare(b.yyyymmdd));

        // Tenants para time-to-first-label.
        const tenantsSnap = await getDocs(collection(firestore, 'tenants'));
        const ttfBuckets: Record<TtfBucket, number> = {
          LT_1H: 0,
          D_1H_24H: 0,
          D_1_7D: 0,
          D_8_30D: 0,
          GT_30D: 0,
          NEVER: 0,
        };
        for (const t of tenantsSnap.docs) {
          // #1008: tenant de teste não é onboarding de cliente.
          if (isReportExcludedTenant(t.id)) continue;
          const data = t.data() as Record<string, unknown>;
          const createdAt = typeof data.createdAt === 'number' ? data.createdAt : null;
          const firstLabelAt = typeof data.firstLabelAt === 'number' ? data.firstLabelAt : null;
          const diff = createdAt !== null && firstLabelAt !== null
            ? firstLabelAt - createdAt
            : null;
          ttfBuckets[bucketTtf(diff)]++;
        }

        const stickinessLatestPct = posthog.length > 0
          ? posthog[posthog.length - 1]?.stickinessPct ?? null
          : null;

        if (guard.cancelled) return;
        setState({
          kind: 'READY',
          posthog,
          timeToFirstLabel: ttfBuckets,
          stickinessLatestPct,
        });
      } catch (err) {
        if (guard.cancelled) return;
        console.error(`${TAG}: failed`, err);
        // #1021 (2026-10-23): a falha só ia para o console — invisível em produção (sem DevTools do cliente).
        reportNonFatal('admin_use_product_failed', {
          site: 'failed',
          code: (err as { code?: string }).code ?? null,
          ...serializeError(err),
        });
        setState({
          kind: 'ERROR',
          error: err instanceof Error ? err.message : String(err),
        });
      }
    }
    void load();
    return (): void => {
      guard.cancelled = true;
    };
  }, [periodFrom, periodTo]);

  return state;
}
